import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Sparkles, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { API } from "@/lib/api";
import { LOGO_URL } from "@/lib/quoteContent";

const BOOKED_LINES = {
  video:
    "Pop open something bubbly. You've just booked a film you'll watch for a lifetime.",
  photo:
    "Pop open something bubbly. You've just booked photographs you'll treasure for a lifetime.",
  both:
    "Pop open something bubbly. You've just booked photos and a film you'll treasure for a lifetime.",
};

const NEXT_STEPS = [
  ["01", "Booking confirmed", "Congrats! Your booking is officially locked in and your date is in our diary!"],
  ["02", "Welcome email", "A welcome from the team is on its way to your inbox."],
  ["03", "Booking & planning app", "We'll email you install instructions to plan every detail."],
];

function detectCoverage(pkg) {
  const text = `${pkg.name} ${pkg.subtitle || ""}`.toLowerCase();
  const hasPhoto = /photo/.test(text);
  const hasVideo = /video|film|highlight|cinema/.test(text.replace(/photograph\w*/g, ""));
  return hasPhoto && hasVideo ? "both" : hasPhoto ? "photo" : "video";
}

function VerifyingScreen() {
  return (
    <div
      className="min-h-screen bg-[#21201F] text-white flex flex-col items-center justify-center px-6 text-center"
      data-testid="payment-verifying"
    >
      <Loader2 className="w-10 h-10 text-[#CCBA56] animate-spin mb-6" strokeWidth={1.25} />
      <h1 className="font-display text-3xl md:text-4xl tracking-tight mb-3">
        Confirming your deposit…
      </h1>
      <p className="text-white/60 text-sm">This only takes a few seconds.</p>
    </div>
  );
}

function PaymentIssueScreen({ phase, slug }) {
  return (
    <div
      className="min-h-screen bg-[#21201F] text-white flex flex-col items-center justify-center px-6 text-center"
      data-testid="payment-issue"
    >
      <h1 className="font-display text-4xl md:text-5xl tracking-tight mb-6">
        {phase === "failed" ? "Payment not completed" : "Still confirming…"}
      </h1>
      <p className="text-white/70 max-w-md mb-10 leading-relaxed">
        {phase === "failed"
          ? "Your payment session expired before completing. Your date isn't secured yet, so head back to your quote to try again."
          : "We couldn't confirm your payment just yet. If you completed payment, don't worry, we'll email you as soon as it lands. Otherwise, head back to your quote."}
      </p>
      <Link
        to={`/q/${slug}`}
        data-testid="back-to-quote-btn"
        className="inline-flex items-center gap-2 bg-[#CCBA56] hover:bg-[#B5A244] text-[#1F1E1D] px-10 py-4 text-xs tracking-overline uppercase font-medium transition-colors"
      >
        Back to your quote
      </Link>
    </div>
  );
}

function useWelcomeData(slug) {
  const [names, setNames] = useState("");
  const [coverage, setCoverage] = useState("video");

  useEffect(() => {
    axios
      .get(`${API}/public/quotes/${slug}`)
      .then((r) => {
        setNames(`${r.data.partner1_name} & ${r.data.partner2_name}`);
        const pkg = (r.data.packages || []).find(
          (p) => p.id === r.data.acceptance?.selected_package_id
        );
        if (pkg) setCoverage(detectCoverage(pkg));
      })
      .catch((e) => console.warn("Welcome data fetch failed", e));
  }, [slug]);

  return { names, coverage };
}

function usePaymentPolling(sessionId) {
  const [phase, setPhase] = useState(sessionId ? "verifying" : "welcome");
  const [depositPaid, setDepositPaid] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;
    async function poll() {
      if (cancelled) return;
      if (attempts.current >= 15) {
        setPhase("timeout");
        return;
      }
      attempts.current += 1;
      try {
        const r = await axios.get(`${API}/public/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setDepositPaid(true);
          setPhase("welcome");
          return;
        }
        if (r.data.status === "expired") {
          setPhase("failed");
          return;
        }
      } catch (e) {
        if (e?.response?.status === 404) {
          setPhase("failed");
          return;
        }
      }
      setTimeout(poll, 2000);
    }
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return { phase, depositPaid };
}

export default function AcceptedThanks() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { names, coverage } = useWelcomeData(slug);
  const { phase, depositPaid } = usePaymentPolling(sessionId);

  if (phase === "verifying") return <VerifyingScreen />;
  if (phase === "failed" || phase === "timeout")
    return <PaymentIssueScreen phase={phase} slug={slug} />;

  return (
    <div
      className="min-h-screen bg-[#21201F] text-white flex flex-col items-center justify-center px-6 py-20 text-center"
      data-testid="accepted-page"
    >
      <div className="fade-up max-w-2xl">
        <img
          src={LOGO_URL}
          alt="Wedding Video Essex"
          className="h-20 md:h-24 w-auto object-contain brightness-0 invert opacity-95 mx-auto mb-10"
        />
        <Sparkles className="w-8 h-8 text-[#CCBA56] mx-auto mb-5" strokeWidth={1.25} />
        <div className="text-xs tracking-overline uppercase text-[#CCBA56] mb-4 font-medium">
          Your date is secured
        </div>
        <h1 className="font-display text-5xl md:text-7xl leading-none tracking-tight mb-8">
          Welcome to the family{names ? "," : "!"}
          {names && (
            <>
              <br />
              <span className="italic font-light">{names}!</span>
            </>
          )}
        </h1>
        {depositPaid && (
          <div
            data-testid="deposit-confirmed"
            className="inline-flex items-center gap-2 bg-white/10 border border-[#CCBA56]/40 px-6 py-3 mb-8 text-sm text-white/90"
          >
            <CheckCircle2 className="w-4 h-4 text-[#CCBA56]" strokeWidth={2} />
            Deposit received, thank you!
          </div>
        )}
        <p className="text-white/70 leading-relaxed mb-10 max-w-xl mx-auto">
          {BOOKED_LINES[coverage]} Your booking is officially locked in and
          your date is in our diary! A welcome email is on its way, followed
          by install instructions for our booking & planning app where we'll
          plan every detail together.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12 text-left">
          {NEXT_STEPS.map(([n, t, d]) => (
            <div key={n} className="bg-white/5 border border-white/10 p-5">
              <div className="text-[#CCBA56] font-display text-2xl mb-2">{n}</div>
              <div className="text-sm font-medium mb-1">{t}</div>
              <div className="text-xs text-white/60 leading-relaxed">{d}</div>
            </div>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="https://wa.link/aorw15"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="welcome-whatsapp-btn"
            className="inline-flex items-center gap-2 bg-[#CCBA56] hover:bg-[#B5A244] text-[#1F1E1D] px-8 py-4 text-xs tracking-overline uppercase font-medium transition-colors"
          >
            Say hi on WhatsApp
          </a>
          <a
            href="https://weddingvideo.co.uk"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 border border-white/30 text-white hover:bg-white/10 px-8 py-4 text-xs tracking-overline uppercase font-medium transition-colors group"
          >
            Visit weddingvideo.co.uk
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
          </a>
        </div>
      </div>
    </div>
  );
}
